<template>
    <div class="container container--utilisateur">  

        <Loader v-if="!ready"></Loader>

        <article class="utilisateur" v-if="ready">

            <header class="utilisateur__heading">
                <div class="utilisateur__heading__top">

                    <h1 class="woocommerce-order-details__title">{{ utilisateur.name }}</h1>

                    <div class="utilisateur__heading__actions">
                        <a :class="['button', { isDisabled: !utilisateur.isImpersonatable }]"
                            :href="impersonateLink"
                            :title="utilisateur.isImpersonatable ? 'Voir l\'interface en tant que ' + utilisateur.name : 'Cet utilisateur ne peut être incarné.'"
                            :aria-disabled="!utilisateur.isImpersonatable"
                            :tabindex="!utilisateur.isImpersonatable ? -1 : undefined"
                            @click="!utilisateur.isImpersonatable && $event.preventDefault()"
                            v-if="$can('INCARNER_UTILISATEURS')"
                        >
                            Voir en tant que
                        </a>
                        <button class="button alt" title="Modifier"
                            v-if="$can('CREER_MODIFIER_UTILISATEURS') && $setting('USER_UPDATE_ENABLED')"
                            v-on:click="edit()">Modifier</button>
                    </div>

                </div>

                <div class="utilisateur__heading__email">
                    <p>
                        <strong>Valide du : </strong> <Date :date="utilisateur.validiteDebut" /> au <Date :date="utilisateur.validiteFin" />
                        &nbsp;
                        <Badge type="success" v-if="utilisateur.isCreated && utilisateur.isActif">Actif</Badge>
                        <Badge type="secondary" v-if="utilisateur.isCreated && !utilisateur.isActif">Désactivé</Badge>
                        <Badge type="warning" v-if="!utilisateur.isCreated">En cours de création</Badge>
                    </p>
                </div>

            </header>  

            <div class="utilisateur__details">
                <ul class="woocommerce-order-overview woocommerce-thankyou-order-details order_details">
                    <li class="woocommerce-order-overview__order order">Nom : <strong>{{ utilisateur.name}}</strong></li>
                    <li class="woocommerce-order-overview__order order">Email : <strong>{{ utilisateur.email}}</strong></li>
                    <li class="woocommerce-order-overview__order order">Code : <strong>{{ utilisateur.code }}</strong></li>
                    <li class="woocommerce-order-overview__order order">Profil : <strong>{{ utilisateur.profil.nom }}</strong></li>
                </ul>
            </div>  

            <div class="utilisateur__habilitations">
                <h3>Habilitations</h3>
                <ul class="woocommerce-address-fields__field-wrapper">
                    <li class="form-row form-row-wide"
                        v-for="habilitation in this.utilisateurHabilitations"
                        :key="habilitation.id">
                        {{ habilitation.nom }}
                    </li>
                </ul>
            </div>

            <div class="utilisateur__emails">
                <h3>Emails à mettre en copie</h3>

                <ul v-if="utilisateur.emailsCC && utilisateur.emailsCC.length"
                    class="woocommerce-address-fields__field-wrapper">
                    <li class="form-row form-row-wide"
                        v-for="email in utilisateur.emailsCC"
                        :key="email">
                        {{ email }}
                    </li>
                </ul>

                <ul v-else>
                    <li class="form-row form-row-wide">Aucune adresse e-mail en copie.</li>
                </ul>
            </div>

            <div class="utilisateur__clients">
                <h3>Adresses</h3>
                
                <!-- Clients -->
                <ul class="clients"
                    v-for="client in clientAddresses"
                    :key="client.id">
                    <li class="clients_item">

                        <div class="client">

                            <collapse :hidden="false" :selected="true">

                                <div slot="collapse-header">

                                    <div class="client__name">
                                        <strong>Client {{ client.clientLabel }}
                                            <span v-if="client.addresses.length > 1">({{ client.addresses.length }} adresses)</span>
                                            <span v-if="client.addresses.length <= 1">({{ client.addresses.length }} adresse)</span>
                                        </strong>
                                    </div>

                                </div>

                                <div slot="collapse-body">                                     
                                    <!-- Adresses client -->
                                    <ul class="client__addresses">
                                        <li class="client__addresses__item"
                                            v-for="address in client.addresses"
                                            :id="'clientAddress_' + address.id"
                                            :key="address.id">

                                            <div class="client-address"> 
                                                <div class="client-address__content">       
                                                    <label class="woocommerce-form__label woocommerce-form__label-for-checkbox"
                                                        :for="'clientAddress_' + address.id">
        
                                                        <span class="client-address__content__name">                           
                                                            <strong>{{ address.name }}</strong><br />
                                                            <span>{{ address.label }}</span><br />
                                                            <small>{{ address.homePhone }}</small>                                
                                                        </span>
                                                    </label>
                                                </div>
                                                <div class="client-address__default">
                                                    <Badge type="info" v-if="address.isDefault">Défaut</Badge>
                                                </div>
                                            </div>

                                        </li>
                                    </ul>
                                </div>
                            </collapse>

                        </div>                                

                    </li>
                </ul>
            </div>

        </article>

    </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'

import api from '@/api/proxyWeb/utilisateurs'
import title from "@/containers/title";
import Badge from '@/components/badge/Badge.vue'
import Collapse from '@/components/collapse/Collapse.vue'
import Date from '@/components/date/Date.vue'
import Loader from '@/components/loader/Loader.vue'
import Router, { routes } from '@/router'

import { createUrl } from '@/router'

import { actions as habilitationActions } from '@/store/habilitations'

export default {

    name: 'Utilisateur',

    data() {
        return {
            utilisateur: null,
            utilisateurAddresses: null,
            utilisateurClients: null,
            utilisateurHabilitations: null,
            changes: 0,
            toastIndex: 0,
            ready: false,

            startDate: null,
            endDate: null,
        }   
    },

    computed: mapState({

        impersonateLink: function() {
            return createUrl(this.$config.ACCOUNT_URL, { action: 'impersonate', user_id: this.utilisateur.userId })
        },

        isMe: function(state) {
            return ( this.utilisateur.userId === state.utilisateurs.current.userId )
        },

        habilitations: state => state.habilitations.all,

        clientAddresses() {
            let clientAddresses = []; 

            this.utilisateurClients.forEach(client => {
                let clientData = {
                    clientId: client.id,
                    clientLabel: client.label,
                    addresses: []
                };

                client.clientAddresses.forEach(clientAddress => {
                    if (this.isAddressChecked(clientAddress)) {
                        clientData.addresses.push({
                            id: clientAddress.id,
                            name: clientAddress.name,
                            label: clientAddress.address.label,
                            homePhone: clientAddress.address.homePhone,
                            isDefault: this.isDefaultAddress(clientAddress)
                        });
                    }
                });

                if (clientData.addresses.length > 0) {
                    clientAddresses.push(clientData);
                }
            });

            return clientAddresses;
        }
    }),

    methods: {

        edit: function() {
            Router.push({
                name: routes.UTILISATEUR_EDIT,
                params: { id: this.utilisateur.id } 
            });
        },

        formatDate: function(datetime) {
            return moment(datetime).format('YYYY-MM-DD')
        },


        isAddressChecked: function(clientAddress) {
            
            let checked = false
            this.utilisateurAddresses.forEach(address => {
                if ( address.id === clientAddress.id ) {
                    checked = true
                }
            });

            return checked
        },

        isDefaultAddress: function(clientAddress) {            
            return ( this.utilisateur.defaultAddressId === clientAddress.id )
        },
    },
  
    mounted () {

        Promise.all([
                api.get(this.$route.params.id),
                api.getAddresses(this.$route.params.id, false),
                api.getClients(this.$route.params.id),
                api.getHabilitations(this.$route.params.id, false),
                this.$store.dispatch(habilitationActions.GET_ALL)
            ])
            .then( data => {
                this.utilisateur = data[0]
                this.utilisateurAddresses = data[1]
                this.utilisateurClients = data[2]
                this.utilisateurHabilitations = data[3]
                this.startDate = this.formatDate(this.utilisateur.validiteDebut)
                this.endDate = this.formatDate(this.utilisateur.validiteFin)
                this.ready = true
                this.setTitle(this.utilisateur.name)
            })
            .catch(error => {
                this.$errorPage(error.reason)
            })
    },

    components: {
        Badge,
        Collapse,
        Date,
        Loader
    },

    mixins: [ title ]
}
</script>

<style>

.clients,
.client__addresses {
    list-style: none;
    margin: 0;
    padding: 0;
}

.clients input[type="checkbox"] {
    margin-right: 0.5rem;
}

.client__addresses__item {
    background-color: #f4f4f4;
    margin-bottom: 2px;
    padding: 0.5rem 2rem 0.5rem 6%;
}

.client__name {    
    padding: 1rem;
}

.client-address {
    display: flex;
    flex-direction: row;
}

.client-address__content {
    flex: 1;
}

.client-address__content label {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
}

.client-address__content label input[type="checkbox"] {
    top: 0.5em;
}

.client-address__content__name {
    flex: 1;
}

.client-address__content__name strong {
    line-height: 2em;
}

.client-address__default {
    display: flex;
    flex-direction: row;
    align-items: center;
}

.isDisabled {
  color: currentColor;
  cursor: not-allowed;
  opacity: 0.5;
  pointer-events: none;
  text-decoration: none;
}

.utilisateur {
    position: relative;
}

.utilisateur__heading {
    margin-bottom: 2rem;
}

.utilisateur__heading__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
}

.utilisateur__heading__top > h1 {
    margin: 0;
}

.utilisateur__heading__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
}

.utilisateur__heading__email p {
    margin: 0.5rem 0 0;
}


.utilisateur__clients {
    clear: both;
}

.utilisateur .utilisateur__habilitations ul,
.utilisateur .utilisateur__emails ul {
    list-style: disc outside;
    margin: 1em 0;
    padding-left: 40px;
}

.utilisateur .utilisateur__habilitations ul > li,
.utilisateur .utilisateur__emails ul > li {
    display: list-item;
    list-style: inherit;
    margin: 0;
}

</style>


